/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";

import {EmGroup} from "../../models/em-group";
import {GroupType} from "../../models/group-type";
import {InstanceData} from "../../models/instance-data";
import {ObjectCategory} from '../../models/object-category';
import {ObjectDefinition} from "../../models/object-definition";
import {ObjectInstance} from "../../models/object-instance";

import {GroupService} from "../../services/group.service";
import {ObjectService} from "../../services/object.service";

import {AddToGroupDialogComponent} from "../add-to-group-dialog.component";
import {ConfirmDeleteDialogComponent} from "../confirm-delete-dialog.component";
import {ManageGroupsDialogComponent} from "../manage-groups-dialog.component";
import {RemoveFromGroupDialogComponent} from "../remove-from-group-dialog.component";
import {ShowGroupDialogComponent} from "../show-group-dialog.component";

import {EntityInstanceDialogComponent} from './dialogs/entity-instance-dialog.component';
import {EntityTypeDialogComponent} from "./dialogs/entity-type-dialog.component";
import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from 'ngx-spinner';
import { INFRA_DUMMY_FUNCTION, RESULTS_PER_PAGE } from 'app/app-constants';
import { FunctionParameterDataUi } from './dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';

/** Constant used to set the Page Size for the "RFID Encoding Scheme" Types / Instances displayed on this page */
//export const PageSize_InfrastructureTagsPage = 10;

/** Filter Option representing the "REMOVE FILTER" Option */
const FilterOption_RemoveFilter = { column: null, label: "REMOVE FILTER" } as FilterOption;

/**
 * Page in the Infrastructure Section that displays the "RFID Encoding Scheme" Types / Instances.
 */
@Component({
  templateUrl: './rfid-encoding-schemes.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class RfidEncodingSchemesComponent implements OnInit {

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  mRfidEncodingSchemeObjects: RfidEncodingSchemeObjectUI[] = [];
  mRfidEncodingSchemeInstances: RfidEncodingSchemeInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Types, 2 = Instances

  //Entity Type Dialog Variables
  mEntity: ObjectDefinition;
  mEntityName: string;
  mEntityType = GroupType.OBJECT;
  mEntityTypeName = "RFID Encoding";

  /* 
   * Filter Variables
   */
  /** Filter Options available to the Encoding Scheme Types */
  filterOptionsForTypes: FilterOption[] = [];
  /** Selected Filter Option for the Encoding Scheme Types */
  filterOptionForTypes: FilterOption = FilterOption_RemoveFilter;
  /** Current Value for the Filter Option being used for the Encoding Scheme Types */
  filterValueForTypes: string = "";
  /** Filter options available to the Encoding Scheme Instances */
  filterOptionsForInstances: FilterOption[] = [];
  /** Selected Filter Option for the Encoding Scheme Instances */
  filterOptionForInstances: FilterOption = FilterOption_RemoveFilter;
  /** Current Value for the Filter Option being used for the Encoding Scheme Instances */
  filterValueForInstances: string = "";

  /* 
   * Entity Instance Dialog Variables
   */
  mInstance: ObjectInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: ObjectDefinition;
  mObjectCategory = ObjectCategory.PASSIVE;
  mInstanceType = GroupType.OBJECTINSTANCE;
  mInstanceTypeName = "RFID Encoding";
  mAvailableEntityTypes: ObjectDefinition[];

  /*
   * Paginator Variables for both Types and Instances
   */
  /** # of results to display per page for both Types and Instances */
  // pageSize = PageSize_InfrastructureTagsPage;
  /** Total # of "Encoding Scheme" Types known to the system. (Used to figure out # of pages) */
  totalNumberOfTypes = 0;
  /** Total # of "Encoding Scheme" Instances known to the system. (Used to figure out he # of pages) */
  totalNumberOfInstances = 0;
  /** Current Page being displayed for the "Encoding Scheme" Types */
  currentPageForTypes = 1;
  /** Current Page being displayed for the "Encoding Scheme" Instances */
  currentPageForInstances = 1;

    // List for Dropdown of Results Per Page value
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];

  /**
   * Constructor
   */
  constructor(
    private mRoute: ActivatedRoute,
    private mObjectService: ObjectService,
    private mGroupService: GroupService,
    private spinnerService: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) { }



  /**
   * Initializes the Component. Called by the Angular Component Lifecycle.
   */
  ngOnInit(): void {
    // Update the Filter Options available to both Types and Instances
    this.refreshFilterOptionsForTypes();
    this.refreshFilterOptionsForInstances();

    // Subscribe to the Route Data to get the first page of results that was retrieved in the background
    this.mRoute.data.subscribe(
      ( data: { 
        rfidEncodingSchemes: PagedResults<ObjectDefinition>, 
        rfidEncodingSchemeInstances: PagedResults<ObjectInstance>}
      ) => {
        this.updatePagedTypes(data.rfidEncodingSchemes);
        this.updatePagedInstances(data.rfidEncodingSchemeInstances);
    });
  }

  manageResultsPerPageClickedForTypes(value) {
    this.selectedPageSizeValueForTypes = value;
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  manageResultsPerPageClickedForInstances(value) {
    this.selectedPageSizeValueForInstances = value;
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }

  isSingleRfidEncodingSchemeObjectSelected(): boolean {
    if (this.mRfidEncodingSchemeObjects != null && this.mRfidEncodingSchemeObjects.length > 0) {
      let oneSelected = false;
      for (const rfidEncodingSchemeObject of this.mRfidEncodingSchemeObjects) {
        if (rfidEncodingSchemeObject.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyRfidEncodingSchemeObjectsSelected(): boolean {
    return this.mRfidEncodingSchemeObjects.some(rfidEncodingSchemeObject => rfidEncodingSchemeObject.mSelected);
  }

  isAllRfidEncodingSchemeObjectsSelected(): boolean {
    return this.mRfidEncodingSchemeObjects !== null && this.mRfidEncodingSchemeObjects.every(rfidEncodingSchemeObject => rfidEncodingSchemeObject.mSelected);
  }

  setAllRfidEncodingSchemeObjectsSelected(event: any): void {
    this.mRfidEncodingSchemeObjects.forEach(rfidEncodingSchemeObject => rfidEncodingSchemeObject.mSelected = event.target.checked);
  }

  isSingleRfidEncodingSchemeInstanceSelected(): boolean {
    if (this.mRfidEncodingSchemeInstances != null && this.mRfidEncodingSchemeInstances.length > 0) {
      let oneSelected = false;
      for (const rfidEncodingSchemeInstance of this.mRfidEncodingSchemeInstances) {
        if (rfidEncodingSchemeInstance.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyRfidEncodingSchemeInstancesSelected(): boolean {
    return this.mRfidEncodingSchemeInstances.some(rfidEncodingSchemeInstance => rfidEncodingSchemeInstance.mSelected);
  }

  isAllRfidEncodingSchemeInstancesSelected(): boolean {
    return this.mRfidEncodingSchemeInstances !== null && this.mRfidEncodingSchemeInstances.every(rfidEncodingSchemeInstance => rfidEncodingSchemeInstance.mSelected);
  }

  setAllRfidEncodingSchemeInstancesSelected(event: any): void {
    this.mRfidEncodingSchemeInstances.forEach(rfidEncodingSchemeInstance => rfidEncodingSchemeInstance.mSelected = event.target.checked);
  }

  getSelectedRfidEncodingSchemeObjects(): ObjectDefinition[] {
    const rfidEncodingSchemeObjects: ObjectDefinition[] = [];
    this.mRfidEncodingSchemeObjects.filter(ui => ui.mSelected).forEach(ui => rfidEncodingSchemeObjects.push(ui.mRfidEncodingScheme));
    return rfidEncodingSchemeObjects;
  }

  getSelectedRfidEncodingSchemeObjectIds(): number[] {
    const ids: number[] = [];
    this.mRfidEncodingSchemeObjects.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mRfidEncodingScheme.id));
    return ids;
  }

  getSelectedRfidEncodingSchemeObjectNames(): string[] {
    const names: string[] = [];
    this.mRfidEncodingSchemeObjects.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mRfidEncodingScheme.name));
    return names;
  }

  getSelectedRfidEncodingSchemeInstances(): ObjectInstance[] {
    const rfidEncodingSchemeInstances: ObjectInstance[] = [];
    this.mRfidEncodingSchemeInstances.filter(ui => ui.mSelected).forEach(ui => rfidEncodingSchemeInstances.push(ui.mRfidEncodingSchemeInstance));
    return rfidEncodingSchemeInstances;
  }

  getSelectedRfidEncodingSchemeInstanceIds(): number[] {
    const ids: number[] = [];
    this.mRfidEncodingSchemeInstances.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mRfidEncodingSchemeInstance.id));
    return ids;
  }

  getSelectedRfidEncodingSchemeInstanceNames(): string[] {
    const names: string[] = [];
    this.mRfidEncodingSchemeInstances.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mRfidEncodingSchemeInstance.name));
    return names;
  }

  manageRfidEncodingSchemeObjectGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.OBJECT);
  }

  manageRfidEncodingSchemeInstanceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.OBJECTINSTANCE);
  }

  addSelectedRfidEncodingSchemeObjectsToGroupClicked(): void {
    if (this.isAnyRfidEncodingSchemeObjectsSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.OBJECT, this.getSelectedRfidEncodingSchemeObjectNames());
    }
  }

  addSelectedRfidEncodingSchemeInstancesToGroupClicked(): void {
    if (this.isAnyRfidEncodingSchemeInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.OBJECTINSTANCE, this.getSelectedRfidEncodingSchemeInstanceNames());
    }
  }

  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedRfidEncodingSchemeObjectIds())
          .then(
            success => {
              this.refreshTypes();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedRfidEncodingSchemeInstanceIds())
          .then(
            success => {
              this.refreshInstances();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }

  removeSelectedRfidEncodingSchemeObjectsFromGroupClicked(): void {
    if (this.isAnyRfidEncodingSchemeObjectsSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const rfidEncodingSchemeObjectNames: string[] = [];
      this.getSelectedRfidEncodingSchemeObjects().forEach(rfidEncodingSchemeObject => {
        rfidEncodingSchemeObjectNames.push(rfidEncodingSchemeObject.name);
        if (rfidEncodingSchemeObject.groups != null) {
          rfidEncodingSchemeObject.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, rfidEncodingSchemeObjectNames);
    }
  }

  removeSelectedRfidEncodingSchemeInstancesFromGroupClicked(): void {
    if (this.isAnyRfidEncodingSchemeObjectsSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const rfidEncodingSchemeInstanceNames: string[] = [];
      this.getSelectedRfidEncodingSchemeInstances().forEach(rfidEncodingSchemeInstance => {
        rfidEncodingSchemeInstanceNames.push(rfidEncodingSchemeInstance.name);
        if (rfidEncodingSchemeInstance.groups != null) {
          rfidEncodingSchemeInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, rfidEncodingSchemeInstanceNames);
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedRfidEncodingSchemeObjectIds())
          .then(
            success => {
              this.refreshTypes();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedRfidEncodingSchemeInstanceIds())
          .then(
            success => {
              this.refreshTypes();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }

  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.OBJECT: {
        this
          .mGroupService
          .getAllObjectsInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.OBJECTINSTANCE: {
        this
          .mGroupService
          .getAllObjectInstancesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }

  addRfidEncodingSchemeClicked(): void {
    const newEntity = new ObjectDefinition();
    newEntity.category = this.mObjectCategory;

    this.mEntity = newEntity
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.showDialog();

  }

  editSelectedRfidEncodingSchemeClicked(): void {
    if (this.isSingleRfidEncodingSchemeObjectSelected()) {
      this.editRfidEncodingScheme(this.getSelectedRfidEncodingSchemeObjects()[0]);
    }
  }

  duplicateSelectedRfidEncodingSchemeClicked(): void {
    if (this.isSingleRfidEncodingSchemeObjectSelected()) {
      this
        .mObjectService
        .getObjectForId(this.getSelectedRfidEncodingSchemeObjects()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }
            if (response.functions != null) {
              response.functions.forEach(f => f.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedRfidEncodingSchemesClicked(): void {
    if (this.isAnyRfidEncodingSchemeObjectsSelected()) {
      this.mDeleteFlag = 1; //1 = Rfid Encoding Schemes
      this.mConfirmDeleteDialog.show("RFID Encoding Type(s)", this.getSelectedRfidEncodingSchemeObjectNames());
    }
  }

  editRfidEncodingScheme(rfidEncodingScheme: ObjectDefinition): void {
    this
      .mObjectService
      .getObjectForId(rfidEncodingScheme.id)
      .then(
        response => {
          //Display the Dialog to edit the Person Type
          if (response != null) {
            //Update the reference to the "Parent" Type to the one that will be in the list of options if the Person Type has one
            if (response.parent != null) {
              const parentUi = this.mRfidEncodingSchemeObjects.find(ui => ui.mRfidEncodingScheme.id == response.parent.id);
              if (parentUi == null) {
                response.parent = null;
              } else {
                response.parent = parentUi.mRfidEncodingScheme;
                console.log("Setting Parent Type to '" + response.parent.id + "/" + response.parent.name + "'...");
              }
            }

            this.mEntity = response;
            this.mEntityName = response.name;
            if (this.mEntity.parent != null) {
              this.mEntityTypeDialogComponent.entityParent = this.mEntity.parent;
            } else {
              this.mEntityTypeDialogComponent.entityParent = null;
            }
            this.mEntityTypeDialogComponent.mParents = this.mRfidEncodingSchemeObjects.map(ui => ui.mRfidEncodingScheme);
            this.mEntityTypeDialogComponent.showDialog();
          }
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Rfid Encoding Scheme ... Create it
      this
        .mObjectService
        .createObject(this.mEntity)
        .then(
          response => {
            this.refreshTypes();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Rfid Encoding Scheme... Update it
      this
        .mObjectService
        .updateObject(this.mEntity)
        .then(
          response => {
            this.refreshTypes();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  addRfidEncodingSchemeInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new ObjectInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataAndShowDialog();
  }

  editSelectedRfidEncodingSchemeInstanceClicked(): void {
    if (this.isSingleRfidEncodingSchemeInstanceSelected()) {
      this.editRfidEncodingSchemeInstance(this.getSelectedRfidEncodingSchemeInstances()[0]);
    }
  }

  duplicateSelectedRfidEncodingSchemeInstanceClicked(): void {
    if (this.isSingleRfidEncodingSchemeInstanceSelected()) {
      this
      .mObjectService
      .getObjectInstanceForId(this.getSelectedRfidEncodingSchemeInstances()[0].id)
      .then(
        response => {
          //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
          this.mInstance = response;

          //Set the Instance Name value so that it gets properly displayed in the Dialog Header
          this.mInstanceName = response.name;

          //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
          this.mInstance.id = null;
          this.mInstance.uuid = null;
          if (this.mInstance.data != null) {
            this.mInstance.data.forEach(d => d.id = null);
          }

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
    }
  }

  deleteSelectedRfidEncodingSchemeInstancesClicked(): void {
    if (this.isAnyRfidEncodingSchemeInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Rfid Encoding Scheme Instances
      this.mConfirmDeleteDialog.show("RFID Encoding(s)", this.getSelectedRfidEncodingSchemeInstanceNames());
    }
  }

  editRfidEncodingSchemeInstance(rfidEncodingSchemeInstance: ObjectInstance): void {
    this
      .mObjectService
      .getObjectInstanceForId(rfidEncodingSchemeInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataAndShowDialog(): Promise<void> {
    //Create a List of the available Rfid Encoding Schemes that the Instance can be a type of
    this.mAvailableEntityTypes = await this.mObjectService.getAllRfidEncodingSchemeObjects();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.object == null? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.object.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.functions = this.mInstance.object.functions;
      this.mInstanceEntityType.properties = this.mInstance.object.properties;
    }

    // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
    this.cdr.detectChanges();

    //Refresh the Instance Data, loading the existing values in for the Instance being edited
    this.mEntityInstanceDialogComponent.refreshData();

    //Show the Entity Instance Dialog
    this.mEntityInstanceDialogComponent.showDialog();
  }

  createOrUpdateEntityInstance(): void {
    //Set the values to the Rfid Encoding Scheme Instance to be created
    this.mInstance.object = this.mInstanceEntityType;

    if (this.mInstance.id == null) {
      //ID is null, Rfid Encoding Scheme Instance does not exist!  Create it...
      this
        .mObjectService
        .createObjectInstance(this.mInstance)
        .then(
          response => {
            this.refreshInstances();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID is not null, Rfid Encoding Scheme Instance exists!  Update it...
      this
        .mObjectService
        .updateObjectInstance(this.mInstance)
        .then(
          response => {
            this.refreshInstances();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  selectedEntityTypeChanged(entityType: ObjectDefinition): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mObjectService
        .getObjectForId(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.functions = foundEntityType.functions;
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }

  refreshInstanceData(): void {
    this
      .mObjectService
      .getObjectInstanceForId(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }

  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Rfid Encoding Scheme Types
      this
        .mObjectService
        .deleteObjects(this.getSelectedRfidEncodingSchemeObjectIds())
        .then(
          response => {
            this.refreshTypes();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) {   //2 = Rfid Encoding Scheme Instances
      this
        .mObjectService
        .deleteObjectInstances(this.getSelectedRfidEncodingSchemeInstanceIds())
        .then(
          response => {
            this.refreshInstances();
          },
          error => this.handleError(error)
        );
    }
  }

  /**
   * Refreshes the "Filter Options" available to the User for the 
   * Encoding Scheme Types. 
   * 
   * This currently just adds the "REMOVE FILTER" and "Name" options.
   */
  private refreshFilterOptionsForTypes(): void {
    // Remove any existing Filters...
    while (this.filterOptionsForTypes.length > 0) {
      this.filterOptionsForTypes.pop();
    }

    // Add the "REMOVE FILTER" Option
    this.filterOptionsForTypes.push(FilterOption_RemoveFilter);
    // Add the "Name" Option
    this.filterOptionsForTypes.push({ column: "name", label: "Name" } as FilterOption);
  }

  /**
   * Called by the UI when a Filter Option is clicked on in the drop
   * down for the "Encoding Scheme" Types.
   * 
   * If the selected "Filter Option" is changing, then this resets
   * the Filter value back to an empty string, sets the current
   * page to the first page and refreshes the data. 
   */
  onFilterOptionForTypesClicked(filterOption: FilterOption): void {
    if (this.filterOptionForTypes == filterOption) {
      return;
    }

    this.filterOptionForTypes = filterOption;
    this.filterValueForTypes = "";
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }

  /**
   * Called by the UI when the "Filter Value" for the "Encoding Scheme"
   * Types is changed.  This sets the current page to the first page
   * and refreshes the data with the filter value changes.
   */
  onFilterValueForTypesChanged(): void {
    this.currentPageForTypes = 1;
    this.refreshTypes();
  }



  /**
   * Refreshes the "Filter Options" available to the User for the 
   * Encoding Scheme Instances. 
   * 
   * This currently just adds the "REMOVE FILTER" and "Name" options.
   */
  private refreshFilterOptionsForInstances(): void {
    // Remove any existing Filters...
    while (this.filterOptionsForInstances.length > 0) {
      this.filterOptionsForInstances.pop();
    }

    // Add the "REMOVE FILTER" Option
    this.filterOptionsForInstances.push(FilterOption_RemoveFilter);
    // Add the "Name" Option
    this.filterOptionsForInstances.push({ column: "name", label: "Name" } as FilterOption);
  }

  /**
   * Called by the UI when a Filter Option is clicked on in the drop
   * down for the "Encoding Scheme" Instances.
   * 
   * If the selected "Filter Option" is changing, then this resets
   * the Filter value back to an empty string, sets the current
   * page to the first page and refreshes the data. 
   */
  onFilterOptionForInstancesClicked(filterOption: FilterOption): void {
    if (this.filterOptionForInstances == filterOption) {
      return;
    }

    this.filterOptionForInstances = filterOption;
    this.filterValueForInstances = "";
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }

  /**
   * Called by the UI when the "Filter Value" for the "Encoding Scheme"
   * Instances is changed.  This sets the current page to the first page
   * and refreshes the data with the filter value changes.
   */
  onFilterValueForInstancesChanged(): void {
    this.currentPageForInstances = 1;
    this.refreshInstances();
  }



  /**
   * Called by the UI when the current Page for the "Encoding Scheme"
   * Types is being changed.  If changing to a valid page number,
   * this updates the current page being displayed along with it's data.
   */
  onTypePageChange(pageNumber?: number): void {
    // Make sure the page number is valid... If not, ignore the call.
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    // Make sure the page number is changing... If not, ignore the call
    if (this.currentPageForTypes == pageNumber) {
      return;
    }

    // Update the current page number and refresh the data
    this.currentPageForTypes = pageNumber;
    this.refreshTypes();
  }

  /**
   * Refreshes the "Encoding Scheme" Type data being displayed in the 
   * table based on the current page and filter values. 
   */
  private async refreshTypes(): Promise<void> {
    // Show the spinner (see this being done in the Tags page)
    this.spinnerService.show();

    // Get the results for the current page / filtering
    let pagedData: PagedResults<ObjectDefinition>;
    try {
      pagedData = await this.mObjectService.getAllObjectsForCategoryPaging(ObjectCategory.PASSIVE, (this.currentPageForTypes - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.filterValueForTypes);
    } catch (error) {
      // Hide the Spinner / Handle the Error
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    // Update the Page Data and hide the Spinner
    this.updatePagedTypes(pagedData);
    this.spinnerService.hide();
  }

  /**
   * Updates the current "Encoding Scheme" Types page information
   * with the data from the given PagedResults<ObjectDefinition>. 
   */
  private updatePagedTypes(pagedData: PagedResults<ObjectDefinition>): void {
    // Create the UI Objects for the Types, update the [] being displayed by the UI
    const uiObjects: RfidEncodingSchemeObjectUI[] = [];
    if (pagedData.data != null) {
      pagedData.data.forEach(rfidEncodingScheme => {
        const ui = new RfidEncodingSchemeObjectUI();
        ui.mRfidEncodingScheme = rfidEncodingScheme;
        ui.mSelected = false;
        uiObjects.push(ui);
      });
    }
    this.mRfidEncodingSchemeObjects = uiObjects;
    
    // Update the Pagination variables 
    this.totalNumberOfTypes = pagedData.size;
  }



  /**
   * Called by the UI when the current Page for the "Encoding Scheme"
   * Instances is being changed.  If changing to a valid page number,
   * this updates the current page being displayed along with it's data.
   */
  onInstancePageChange(pageNumber?: number): void {
    // Make sure the page number is valid... If not, ignore the call.
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    // Make sure the page number is changing... If not, ignore the call
    if (this.currentPageForInstances == pageNumber) {
      return;
    }

    // Update the current page number and refresh the data
    this.currentPageForInstances = pageNumber;
    this.refreshInstances();
  }

  /**
   * Refreshes the "Encoding Scheme" Instance data being displayed in 
   * the table based on the current page and filter values. 
   */
  private async refreshInstances(): Promise<void> {
    // Show the spinner (see this being done in the Tags page)
    this.spinnerService.show();

    // Get the results for the current page / filtering
    let pagedData: PagedResults<ObjectInstance>;
    try {
      pagedData = await this.mObjectService.getAllObjectInstancesForCategoryPaging(ObjectCategory.PASSIVE, (this.currentPageForInstances - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.filterValueForInstances);
    } catch (error) {
      // Hide the Spinner / Handle the Error
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    // Update the Page Data and hide the Spinner
    this.updatePagedInstances(pagedData);
    this.spinnerService.hide();
  }

  /**
   * Updates the current "Encoding Scheme" Instances page information
   * with the data from the given PagedResults<ObjectInstance>. 
   */
  private updatePagedInstances(pagedData: PagedResults<ObjectInstance>): void {
    // Create the InstanceUI Objects for the Instances, update the [] being displayed by the UI
    const uiObjects: RfidEncodingSchemeInstanceUI[] = [];
    if (pagedData.data != null) {
      pagedData.data.forEach(rfidEncodingSchemeInstance => {
        const ui = new RfidEncodingSchemeInstanceUI();
        ui.mRfidEncodingSchemeInstance = rfidEncodingSchemeInstance;
        ui.mSelected = false;
        uiObjects.push(ui);
      });
    }
    this.mRfidEncodingSchemeInstances = uiObjects;
    
    // Update the Pagination variables 
    this.totalNumberOfInstances = pagedData.size;
  }

  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    } 
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

  /*  
*  Handling the Right click event only on RFID-Encoding Instance Name Column
*/
  fetchFunctionList(event: MouseEvent, rfidEncodingInstance: ObjectInstance ): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = []

    // Send the request to get the RFID-Encoding Instance details using the ID
    this
      .mObjectService
      .getObjectInstanceForId(rfidEncodingInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the drop-down to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.data && Array.isArray(response.object.functions) && (response.object.functions).length > 0) {
              this.functionList = response.object.functions;

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error);
          }
        },
        error => this.handleError(error)
      );

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }


}

class RfidEncodingSchemeObjectUI {
  mSelected = false;
  mRfidEncodingScheme: ObjectDefinition;
}

class RfidEncodingSchemeInstanceUI {
  mSelected = false;
  mRfidEncodingSchemeInstance: ObjectInstance;
}

/**
 * Represents a "Filter Option" available to the Users
 * in the "Filter" Button drop down list. 
 */
interface FilterOption {
  column: string;
  label: string;
}

